import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useWebSocket } from "../../context/WebSocketContext";
import { useAuth } from "../../context/AuthContext";

// Map display labels to backend keys
const categoryKeyMap = {
  "Qualities / Personality Traits": "personality_traits",
  "Lifestyle Preferences": "lifestyle_preferences",
  "Relationship Goals": "relationship_goals",
  "Hobbies & Interests": "hobbies",
  "Cultural / Values": "cultural_values",
};

const PreferenceAccordion = ({
  categories,
  selectedPrefs,
  setSelectedPrefs,
  preferences = {},
}) => {
  const { sendMessage, isConnected } = useWebSocket();
  const { user } = useAuth();

  const [expanded, setExpanded] = useState(Object.keys(categories)[0] || "");
  const [transformedPrefs, setTransformedPrefs] = useState({});

  const isOptionSelected = (prefs, displayCategory, option) => {
    const backendCategory = categoryKeyMap[displayCategory];
    return prefs?.[backendCategory]?.[option] === true;
  };

  // Sync preferences into internal transformed state and update parent selectedPrefs
  useEffect(() => {
    const transformed = {};

    Object.entries(preferences).forEach(([category, values]) => {
      if (Array.isArray(values)) {
        transformed[category] = {};
        values.forEach((val) => {
          transformed[category][val] = true;
        });
      } else if (typeof values === "object" && values !== null) {
        transformed[category] = { ...values };
      }
    });

    // console.log("✅ Syncing preferences into selectedPrefs:", transformed);
    setTransformedPrefs(transformed);
    setSelectedPrefs(transformed);
  }, [preferences, setSelectedPrefs]);

  const handleAccordionChange = (category) => (_, isExpanded) => {
    setExpanded(isExpanded ? category : false);
  };

  const handleCheckboxChange = (displayCategory, label) => (event) => {
    const checked = event.target.checked;
    const backendCategory = categoryKeyMap[displayCategory];

    const preferenceUpdate = {
      user_id: user?.user_id,
      tag: "interests_update",
      payload: {
        category: backendCategory,
        option: label,
      },
    };

    // console.log("📤 Sending preference update:", preferenceUpdate);

    if (isConnected) {
      sendMessage(preferenceUpdate);
    } else {
      console.warn("⚠️ WebSocket not connected. Message not sent.");
    }

    setSelectedPrefs((prev) => ({
      ...prev,
      [backendCategory]: {
        ...(prev[backendCategory] || {}),
        [label]: checked,
      },
    }));

    setTransformedPrefs((prev) => ({
      ...prev,
      [backendCategory]: {
        ...(prev[backendCategory] || {}),
        [label]: checked,
      },
    }));
  };

  //   console.log("🗂️ Categories:", categories);

  return (
    <div>
      {Object.entries(categories).map(([displayCategory, options]) => (
        <Accordion
          key={displayCategory}
          expanded={expanded === displayCategory}
          onChange={handleAccordionChange(displayCategory)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: "bold" }}>
              {displayCategory}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup>
              {options.map((option) => {
                const isChecked = isOptionSelected(
                  transformedPrefs,
                  displayCategory,
                  option
                );

                // console.log(
                //   `🔍 Checkbox: [${displayCategory}] "${option}" → ${isChecked}`
                // );

                return (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={handleCheckboxChange(displayCategory, option)}
                      />
                    }
                    label={option}
                    sx={{ color: "black" }}
                  />
                );
              })}
            </FormGroup>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};

export default PreferenceAccordion;
